import CloseIcon from '@mui/icons-material/Close';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { LoadingButton } from '@mui/lab';
import { Box, Card, CardContent, IconButton, Modal, TextField, Typography } from '@mui/material';
import { Confirm, Report } from 'notiflix';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { transferMoves } from '../../data/api/incomes/IncomeRoutes';
import useIndex from '../../hooks/useIndex';
import validErrors from '../../hooks/validErrors';

/**
 * Modal reutilizable para transferir jugadas (income_codes ya asignados) del
 * usuario autenticado a otro usuario. Pensado para que una Empresa entregue
 * jugadas a sus distribuidores.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - targetUser: usuario destino (debe tener id, name, lastname, moves)
 *  - onSuccess: callback opcional que recibe el payload del backend
 */
const AssignMovesModal = ({ open, onClose, targetUser, onSuccess }) => {
    const { auth, token, validateUser } = useIndex();

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: { moves: '' },
    });

    const [submitting, setSubmitting] = useState(false);
    const availableMoves = Number(auth?.moves ?? 0);

    useEffect(() => {
        if (open) reset({ moves: '' });
    }, [open]);

    const handleClose = () => {
        if (submitting) return;
        reset();
        onClose?.();
    };

    const sendTransfer = async (moves) => {
        setSubmitting(true);
        try {
            const { data } = await transferMoves(token, {
                to_user_id: targetUser?.id,
                moves,
            });
            if (data?.success) {
                Report.success('Éxito', data?.message ?? 'Jugadas transferidas.', 'Aceptar', { textAlign: 'center' });
                if (typeof validateUser === 'function') validateUser();
                onSuccess?.(data?.data);
                onClose?.();
                reset();
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setSubmitting(false);
        }
    };

    const onSubmit = (form) => {
        const moves = Number(form.moves);
        if (!moves || moves <= 0) return;
        if (moves > availableMoves) return;

        Confirm.show(
            'Confirmar transferencia',
            `¿Transferir ${moves} jugadas a ${targetUser?.name} ${targetUser?.lastname ?? ''}?`,
            'Sí, transferir',
            'Cancelar',
            () => sendTransfer(moves),
            () => { }
        );
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Center>
                <StyledCard>
                    <CardContent sx={{ p: 2.5 }}>
                        <Header>
                            <Box className="title-wrap">
                                <Box className="icon-box">
                                    <SwapHorizIcon />
                                </Box>
                                <Box>
                                    <Typography className="title">Asignar jugadas</Typography>
                                    <Typography className="subtitle">
                                        Transfiere jugadas a un distribuidor de tu empresa.
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton onClick={handleClose} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Header>

                        <TransferGrid>
                            <PartyCard>
                                <Box className="party-icon">
                                    <BusinessOutlinedIcon />
                                </Box>
                                <Box className="party-body">
                                    <span className="party-label">De</span>
                                    <strong className="party-name">
                                        {auth?.name} {auth?.lastname ?? ''}
                                    </strong>
                                    <Box className="party-pill">
                                        {availableMoves} disponibles
                                    </Box>
                                </Box>
                            </PartyCard>

                            <ArrowBox>
                                <SwapHorizIcon />
                            </ArrowBox>

                            <PartyCard>
                                <Box className="party-icon">
                                    <StorefrontOutlinedIcon />
                                </Box>
                                <Box className="party-body">
                                    <span className="party-label">A</span>
                                    <strong className="party-name">
                                        {targetUser?.name} {targetUser?.lastname ?? ''}
                                    </strong>
                                    <Box className="party-pill party-pill--neutral">
                                        {Number(targetUser?.moves ?? 0)} actuales
                                    </Box>
                                </Box>
                            </PartyCard>
                        </TransferGrid>

                        <form onSubmit={handleSubmit(onSubmit)} noValidate>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Cantidad de jugadas"
                                inputProps={{ min: 1, max: availableMoves, step: 1 }}
                                onWheel={(e) => e.target.blur()}
                                onKeyDown={(e) => {
                                    if (['-', 'e', 'E', '+', '.', ','].includes(e.key)) {
                                        e.preventDefault();
                                    }
                                }}
                                error={!!errors.moves}
                                helperText={errors.moves?.message}
                                sx={fieldStyles}
                                {...register('moves', {
                                    required: 'Indica una cantidad.',
                                    min: { value: 1, message: 'Debe ser mayor a 0.' },
                                    max: { value: availableMoves, message: `No puedes transferir más de ${availableMoves}.` },
                                    valueAsNumber: true,
                                })}
                            />

                            <LoadingButton
                                fullWidth
                                type="submit"
                                variant="contained"
                                loading={submitting}
                                disabled={availableMoves <= 0}
                                startIcon={<SwapHorizIcon />}
                                sx={primaryButtonStyles}
                            >
                                <span>Transferir</span>
                            </LoadingButton>

                            {availableMoves <= 0 && (
                                <Typography className="empty-warning">
                                    No tienes jugadas disponibles para transferir.
                                </Typography>
                            )}
                        </form>
                    </CardContent>
                </StyledCard>
            </Center>
        </Modal>
    );
};

export default AssignMovesModal;

const fieldStyles = {
    mt: 0.5,
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        background: '#fff',
        '& fieldset': { borderColor: 'rgba(15, 34, 77, 0.14)' },
        '&.Mui-focused fieldset': { borderColor: '#d6aa3a' },
    },
};

const primaryButtonStyles = {
    mt: 2,
    borderRadius: '12px',
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
    boxShadow: '0 6px 14px rgba(15, 34, 77, 0.18)',
    '&:hover': {
        background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
    },
};

const Center = styled(Box)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 520px;
    outline: none;
`;

const StyledCard = styled(Card)`
    width: 100%;
    border-radius: 18px !important;
    border: 1px solid rgba(15, 34, 77, 0.1);
    box-shadow: 0 18px 38px rgba(15, 34, 77, 0.18) !important;
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%) !important;
    overflow: hidden;

    &::before {
        content: "";
        display: block;
        height: 5px;
        width: 100%;
        background: linear-gradient(90deg, #10285f 0%, #d6aa3a 100%);
    }

    .empty-warning {
        display: block;
        margin-top: 0.6rem;
        color: #b71c1c;
        font-size: 0.78rem;
        font-weight: 700;
        text-align: center;
    }
`;

const Header = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.6rem;
    margin-bottom: 1.2rem;

    .title-wrap {
        display: flex;
        align-items: center;
        gap: 0.7rem;
        min-width: 0;
    }

    .icon-box {
        width: 40px;
        height: 40px;
        border-radius: 12px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
        color: #10285f;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .title {
        color: #10285f;
        font-weight: 900;
        font-size: 1.05rem;
        line-height: 1.2;
    }

    .subtitle {
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.78rem;
        font-weight: 600;
        margin-top: 0.1rem;
    }
`;

const TransferGrid = styled(Box)`
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 0.6rem;
    align-items: stretch;
    margin-bottom: 1.2rem;

    @media (max-width: 520px) {
        grid-template-columns: 1fr;
    }
`;

const PartyCard = styled(Box)`
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    padding: 0.75rem 0.85rem;
    border-radius: 14px;
    background: rgba(16, 40, 95, 0.045);
    border: 1px solid rgba(15, 34, 77, 0.08);
    min-width: 0;

    .party-icon {
        width: 34px;
        height: 34px;
        border-radius: 10px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.26);
        color: #10285f;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        svg { font-size: 1.1rem; }
    }

    .party-body {
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
    }

    .party-label {
        color: rgba(15, 34, 77, 0.6);
        font-size: 0.7rem;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.4px;
    }

    .party-name {
        color: #10285f;
        font-size: 0.88rem;
        font-weight: 900;
        line-height: 1.2;
        word-break: break-word;
    }

    .party-pill {
        margin-top: 0.25rem;
        align-self: flex-start;
        padding: 0.18rem 0.55rem;
        border-radius: 999px;
        font-size: 0.7rem;
        font-weight: 900;
        color: #0f5c36;
        background: rgba(46, 125, 50, 0.12);
        border: 1px solid rgba(46, 125, 50, 0.25);
    }

    .party-pill--neutral {
        color: #10285f;
        background: rgba(16, 40, 95, 0.10);
        border-color: rgba(16, 40, 95, 0.22);
    }
`;

const ArrowBox = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    color: #d6aa3a;

    svg { font-size: 1.4rem; }

    @media (max-width: 520px) {
        transform: rotate(90deg);
    }
`;
